import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  Badge,
  Button,
  Input,
  Text,
  EmptyState,
  SlideOver,
  Table,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import {
  useCreateSetupIntentMutation,
  useDetachPaymentMethodMutation,
  useGetBillingBudgetQuery,
  useGetBillingProfileQuery,
  useGetInvoicesQuery,
  useGetPaymentMethodsQuery,
  useGetPaymentReadinessQuery,
  useGetStripeConfigQuery,
  useRetryInvoiceMutation,
  useSetDefaultPaymentMethodMutation,
  useUpdateBillingBudgetMutation,
  useUpdateBillingProfileMutation,
  type BillingProfile,
  type Invoice,
} from '../../store/api/billingApi';
import { useGetAnalyticsSummaryQuery } from '../../store/api/analyticsApi';
import { env } from '../../utils/env';
import {
  BillHeaderRow,
  FieldGrid,
  Footnote,
  HeaderActions,
  PanelCard,
  PaymentMethodActions,
  PaymentMethodDetails,
  PaymentMethodIcon,
  PaymentMethodItem,
  ProfileField,
  ProfileFieldLabel,
  ProfileFieldValue,
  ProfileViewGrid,
  ProgressFill,
  ProgressTrack,
  SectionTitle,
} from './Payments.styles';

const EMPTY_PROFILE: BillingProfile = {
  businessName: '',
  billingEmail: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  taxId: '',
};

type AppBudgetRowData = {
  applicationId: string;
  name: string;
  tokenCap: number | null;
  utilisedPct: number | null | undefined;
};

function readinessMessage(reason?: string | null): string {
  switch (reason) {
    case 'amount_due':
      return 'A payment method is required — your current period has an estimated amount due.';
    case 'subscription_active':
      return 'Add a payment method for your subscription before the next invoice.';
    case 'free_period_ending':
      return 'Your free subscription period is ending soon. Add a card to avoid interruption.';
    default:
      return 'Add a payment method to continue billing without interruption.';
  }
}

function errMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data: unknown }).data;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      const msg =
        (data as { message?: string }).message ||
        (data as { error?: string }).error;
      if (msg) return String(msg);
      return JSON.stringify(data);
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatPeriod(inv: Invoice): string {
  if (inv.periodStart) {
    const d = new Date(inv.periodStart);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
    }
  }
  return inv.date || '—';
}

function statusVariant(
  status: Invoice['status'],
): 'success' | 'warning' | 'error' | 'neutral' {
  if (status === 'paid') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'pending') return 'warning';
  return 'neutral';
}

function profileFromApi(profile: BillingProfile): BillingProfile {
  return {
    businessName: profile.businessName || '',
    billingEmail: profile.billingEmail || '',
    addressLine1: profile.addressLine1 || '',
    addressLine2: profile.addressLine2 || '',
    city: profile.city || '',
    state: profile.state || '',
    postalCode: profile.postalCode || '',
    country: profile.country || '',
    taxId: profile.taxId || '',
  };
}

function formatBillingAddress(p: BillingProfile): string {
  const cityLine = [p.city, p.state, p.postalCode].filter(Boolean).join(' ');
  return [p.addressLine1, p.addressLine2, cityLine, p.country]
    .filter(Boolean)
    .join(', ');
}

function displayOrNotSet(value?: string | null): { text: string; muted: boolean } {
  const trimmed = (value || '').trim();
  if (!trimmed) return { text: 'Not set', muted: true };
  return { text: trimmed, muted: false };
}

function formatTokenCap(cap: number | null): string {
  if (cap == null) return '—';
  return cap.toLocaleString('en-US');
}

const SetupForm: React.FC<{
  onDone: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}> = ({ onDone, onCancel, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    onError('');
    const { error } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/payments?tab=billing`,
      },
    });
    setBusy(false);
    if (error) {
      onError(error.message || 'Card setup failed');
      return;
    }
    onDone();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never',
          },
          fields: {
            billingDetails: {
              address: 'auto',
            },
          },
        }}
      />
      <Button variant="primary" onClick={onSubmit} disabled={!stripe || busy}>
        {busy ? 'Saving…' : 'Save card'}
      </Button>
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
    </div>
  );
};

export const BillingPanel: React.FC = () => {
  const navigate = useNavigate();
  const { data: profile, error: profileError } = useGetBillingProfileQuery();
  const [updateProfile, { isLoading: savingProfile }] =
    useUpdateBillingProfileMutation();
  const { data: budget } = useGetBillingBudgetQuery();
  const [updateBudget, { isLoading: savingBudget }] =
    useUpdateBillingBudgetMutation();
  const { data: methods, refetch: refetchMethods } = useGetPaymentMethodsQuery();
  const { data: invoices, refetch: refetchInvoices } = useGetInvoicesQuery();
  const { data: readiness } = useGetPaymentReadinessQuery();
  const { data: stripeConfig } = useGetStripeConfigQuery();
  const { data: analyticsSummary } = useGetAnalyticsSummaryQuery();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [setDefaultPm] = useSetDefaultPaymentMethodMutation();
  const [detachPm] = useDetachPaymentMethodMutation();
  const [retryInvoice, { isLoading: retrying }] = useRetryInvoiceMutation();

  const [form, setForm] = useState<BillingProfile>(EMPTY_PROFILE);
  const [profileEditing, setProfileEditing] = useState(false);
  const [tokenCap, setTokenCap] = useState('');
  const [alertPct, setAlertPct] = useState('85');
  const [budgetDirty, setBudgetDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [cardError, setCardError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const publishableKey =
    stripeConfig?.publishableKey || env.STRIPE_PUBLISHABLE_KEY || '';

  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    if (!publishableKey) return null;
    return loadStripe(publishableKey);
  }, [publishableKey]);

  const orgUtil = analyticsSummary?.budgetUtilisation?.organisation;
  const orgUtilPct = orgUtil?.utilisationPct ?? null;
  const appUtilById = useMemo(() => {
    const map = new Map<string, number | null | undefined>();
    for (const row of analyticsSummary?.budgetUtilisation?.applications || []) {
      if (row.applicationId) map.set(row.applicationId, row.utilisationPct);
    }
    return map;
  }, [analyticsSummary]);

  const displayProfile = profile ? profileFromApi(profile) : EMPTY_PROFILE;

  useEffect(() => {
    if (!budget || budgetDirty) return;
    const cap = budget.organisation?.budgetConfig?.tokenCap;
    const alert = budget.organisation?.budgetConfig?.alertThresholdPct;
    if (cap != null) setTokenCap(String(cap));
    if (alert != null) setAlertPct(String(alert));
  }, [budget, budgetDirty]);

  useEffect(() => {
    if (profileError) {
      setActionError(errMessage(profileError, 'Could not load billing profile'));
    }
  }, [profileError]);

  const startEditProfile = () => {
    setForm(profileFromApi(displayProfile));
    setProfileEditing(true);
    setSavedMsg('');
    setActionError('');
  };

  const cancelEditProfile = () => {
    setForm(profileFromApi(displayProfile));
    setProfileEditing(false);
  };

  const onSaveProfile = async () => {
    setSavedMsg('');
    setActionError('');
    try {
      await updateProfile(form).unwrap();
      setSavedMsg('Billing profile saved.');
      setProfileEditing(false);
    } catch (err) {
      setActionError(errMessage(err, 'Could not save billing profile'));
    }
  };

  const onSaveOrgBudget = async () => {
    setSavedMsg('');
    setActionError('');
    try {
      await updateBudget({
        budgetConfig: {
          tokenCap: tokenCap ? Number(tokenCap) : undefined,
          period: 'monthly',
          alertThresholdPct: alertPct ? Number(alertPct) : undefined,
        },
      }).unwrap();
      setBudgetDirty(false);
      setSavedMsg('Organisation budget saved.');
    } catch (err) {
      setActionError(errMessage(err, 'Could not save organisation budget'));
    }
  };

  const setField =
    (key: keyof BillingProfile) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const closeAddCard = () => {
    setModalOpen(false);
    setClientSecret(null);
    setSetupLoading(false);
    setCardError('');
  };

  const openAddCard = async () => {
    setCardError('');
    setClientSecret(null);
    setModalOpen(true);
    if (!publishableKey) {
      setCardError(
        'Stripe is not configured for this environment. Ask an operator to set publishable_key in lumina/{env}/stripe.',
      );
      return;
    }
    setSetupLoading(true);
    try {
      const intent = await createSetupIntent().unwrap();
      if (!intent?.clientSecret) {
        setCardError('Could not start card setup (missing client secret).');
        return;
      }
      setClientSecret(intent.clientSecret);
    } catch (err: unknown) {
      setCardError(errMessage(err, 'Could not start card setup'));
    } finally {
      setSetupLoading(false);
    }
  };

  const onCardSaved = async () => {
    closeAddCard();
    setSavedMsg('Payment method saved.');
    await refetchMethods();
  };

  const onMakeDefault = async (paymentMethodId: string) => {
    setActionError('');
    try {
      await setDefaultPm({ paymentMethodId }).unwrap();
      setSavedMsg('Default payment method updated.');
    } catch (err) {
      setActionError(errMessage(err, 'Could not update default payment method'));
    }
  };

  const onDetach = async (paymentMethodId: string) => {
    setActionError('');
    try {
      await detachPm({ paymentMethodId }).unwrap();
      setSavedMsg('Payment method removed.');
    } catch (err) {
      setActionError(errMessage(err, 'Could not remove payment method'));
    }
  };

  const onRetryInvoice = async (invoiceId: string) => {
    setActionError('');
    setSavedMsg('');
    setRetryingId(invoiceId);
    try {
      await retryInvoice({ invoiceId }).unwrap();
      setSavedMsg('Invoice payment succeeded.');
      await refetchInvoices();
    } catch (err) {
      setActionError(errMessage(err, 'Could not retry invoice payment'));
    } finally {
      setRetryingId(null);
    }
  };

  const paymentMethods = methods?.paymentMethods || [];
  const invoiceRows = invoices?.invoices || [];

  const appBudgetRows: AppBudgetRowData[] = useMemo(
    () =>
      (budget?.applications || []).map((app) => ({
        applicationId: app.applicationId,
        name: app.name,
        tokenCap: app.budgetConfig?.tokenCap ?? null,
        utilisedPct: appUtilById.get(app.applicationId),
      })),
    [budget?.applications, appUtilById],
  );

  const nameDisplay = displayOrNotSet(displayProfile.businessName);
  const emailDisplay = displayOrNotSet(displayProfile.billingEmail);
  const addressFormatted = formatBillingAddress(displayProfile);
  const addressDisplay = displayOrNotSet(addressFormatted);
  const taxDisplay = displayOrNotSet(displayProfile.taxId);

  const appBudgetColumns: Column<AppBudgetRowData>[] = [
    {
      key: 'name',
      header: 'Application',
      render: (_v, row) => <strong>{row.name}</strong>,
    },
    {
      key: 'tokenCap',
      header: 'Monthly token cap',
      render: (_v, row) => (
        <span style={{ fontFamily: 'ui-monospace, monospace' }}>
          {formatTokenCap(row.tokenCap)}
        </span>
      ),
    },
    {
      key: 'utilisedPct',
      header: 'Utilised',
      render: (_v, row) =>
        row.utilisedPct != null ? `${row.utilisedPct}%` : '—',
    },
    {
      key: 'applicationId',
      header: '',
      render: (_v, row) => (
        <Link to={`/applications/${row.applicationId}`}>Edit on app →</Link>
      ),
    },
  ];

  const invoiceColumns: Column<Invoice>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      render: (_v, row) => (
        <span style={{ fontFamily: 'ui-monospace, monospace' }}>
          {row.invoiceNumber}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Period',
      render: (_v, row) => formatPeriod(row),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_v, row) => (
        <span style={{ fontFamily: 'ui-monospace, monospace' }}>
          ${Number(row.amount).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => (
        <Badge variant={statusVariant(row.status)} size="sm">
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'id',
      header: 'Action',
      render: (_v, row) => {
        if (row.status === 'failed') {
          return (
            <Button
              variant="primary"
              onClick={() => onRetryInvoice(row.id)}
              disabled={retrying && retryingId === row.id}
            >
              {retrying && retryingId === row.id ? 'Retrying…' : 'Retry'}
            </Button>
          );
        }
        return (
          <span style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {row.downloadUrl ? (
              <>
                <a href={row.downloadUrl} target="_blank" rel="noreferrer">
                  View
                </a>
                <a href={row.downloadUrl} target="_blank" rel="noreferrer">
                  Download
                </a>
              </>
            ) : (
              <span style={{ color: 'var(--text-muted)' }}>—</span>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {readiness?.cardRequired ? (
        <PanelCard padding={16}>
          <Text variant="body1" style={{ color: 'var(--warning, #b45309)' }}>
            {readinessMessage(readiness.reason)}
          </Text>
        </PanelCard>
      ) : null}

      <PanelCard padding={24}>
        <BillHeaderRow>
          <SectionTitle style={{ margin: 0 }}>Billing profile</SectionTitle>
          {profileEditing ? (
            <HeaderActions>
              <Button
                variant="primary"
                onClick={onSaveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                onClick={cancelEditProfile}
                disabled={savingProfile}
              >
                Cancel
              </Button>
            </HeaderActions>
          ) : (
            <Button variant="outline" onClick={startEditProfile}>
              Edit profile
            </Button>
          )}
        </BillHeaderRow>

        {profileEditing ? (
          <FieldGrid>
            <Input
              label="Business Name"
              value={form.businessName}
              onChange={setField('businessName')}
            />
            <Input
              label="Billing Email"
              value={form.billingEmail}
              onChange={setField('billingEmail')}
            />
            <Input
              label="Address line 1"
              value={form.addressLine1}
              onChange={setField('addressLine1')}
            />
            <Input
              label="Address line 2"
              value={form.addressLine2}
              onChange={setField('addressLine2')}
            />
            <Input label="City" value={form.city} onChange={setField('city')} />
            <Input
              label="State"
              value={form.state}
              onChange={setField('state')}
            />
            <Input
              label="Postal code"
              value={form.postalCode}
              onChange={setField('postalCode')}
            />
            <Input
              label="Country"
              value={form.country}
              onChange={setField('country')}
            />
            <Input
              label="Tax ID"
              value={form.taxId}
              onChange={setField('taxId')}
            />
          </FieldGrid>
        ) : (
          <ProfileViewGrid>
            <ProfileField>
              <ProfileFieldLabel>Business Name</ProfileFieldLabel>
              <ProfileFieldValue $muted={nameDisplay.muted}>
                {nameDisplay.text}
              </ProfileFieldValue>
            </ProfileField>
            <ProfileField>
              <ProfileFieldLabel>Billing Email</ProfileFieldLabel>
              <ProfileFieldValue $muted={emailDisplay.muted}>
                {emailDisplay.text}
              </ProfileFieldValue>
            </ProfileField>
            <ProfileField $fullWidth>
              <ProfileFieldLabel>Billing Address</ProfileFieldLabel>
              <ProfileFieldValue $muted={addressDisplay.muted}>
                {addressDisplay.text}
              </ProfileFieldValue>
            </ProfileField>
            <ProfileField>
              <ProfileFieldLabel>Tax ID</ProfileFieldLabel>
              <ProfileFieldValue $muted={taxDisplay.muted}>
                {taxDisplay.text}
              </ProfileFieldValue>
            </ProfileField>
          </ProfileViewGrid>
        )}
      </PanelCard>

      <PanelCard padding={24}>
        <BillHeaderRow>
          <SectionTitle style={{ margin: 0 }}>Payment methods</SectionTitle>
          <Button
            variant="outline"
            onClick={openAddCard}
            data-testid="header-add-payment"
          >
            Add payment method
          </Button>
        </BillHeaderRow>
        <Footnote>
          Cards are stored with Stripe. Lumina never stores raw card numbers.
        </Footnote>
        {paymentMethods.length === 0 ? (
          <EmptyState
            title="No payment methods yet"
            description="Add a card anytime — required when an amount is due or your free period ends."
          />
        ) : (
          <div>
            {paymentMethods.map((pm) => (
              <PaymentMethodItem key={pm.id}>
                <PaymentMethodIcon>
                  {(pm.brand || 'CARD').toUpperCase()}
                </PaymentMethodIcon>
                <PaymentMethodDetails>
                  <strong>
                    {(pm.brand || 'Card').replace(/^\w/, (c) => c.toUpperCase())}{' '}
                    ending in {pm.last4}
                  </strong>
                  <span
                    style={{
                      fontSize: '0.8125rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    Expires {pm.expMonth}/{pm.expYear}
                  </span>
                </PaymentMethodDetails>
                <PaymentMethodActions>
                  {pm.isDefault ? (
                    <Badge variant="success" size="sm">
                      Default
                    </Badge>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => onMakeDefault(pm.id)}
                    >
                      Make default
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => onDetach(pm.id)}>
                    Remove
                  </Button>
                </PaymentMethodActions>
              </PaymentMethodItem>
            ))}
          </div>
        )}
      </PanelCard>

      <PanelCard padding={24}>
        <BillHeaderRow>
          <SectionTitle style={{ margin: 0 }}>Budgets</SectionTitle>
          <Button variant="outline" onClick={() => navigate('/analytics')}>
            View utilisation
          </Button>
        </BillHeaderRow>
        <Footnote>
          Set an organisation-wide monthly <strong>token</strong> cap here.
          Application caps are edited on each application’s Configuration tab.
          Staff do not set these limits.
        </Footnote>
        <Text variant="h4" style={{ margin: 0 }}>
          Organisation-wide
        </Text>
        <FieldGrid>
          <Input
            label="Monthly org token limit"
            value={tokenCap}
            onChange={(e) => {
              setBudgetDirty(true);
              setTokenCap(e.target.value);
            }}
            placeholder="e.g. 10000000"
          />
          <Input
            label="Alert threshold (%)"
            value={alertPct}
            onChange={(e) => {
              setBudgetDirty(true);
              setAlertPct(e.target.value);
            }}
            placeholder="85"
          />
        </FieldGrid>
        {orgUtilPct != null ? (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.875rem',
                marginBottom: '0.35rem',
              }}
            >
              <span>Org utilisation this period</span>
              <strong>{orgUtilPct}%</strong>
            </div>
            <ProgressTrack>
              <ProgressFill
                $pct={orgUtilPct}
                $danger={orgUtilPct >= Number(alertPct || 85)}
              />
            </ProgressTrack>
          </div>
        ) : (
          <Footnote>
            Utilisation appears once Analytics has token usage for this period.
          </Footnote>
        )}
        <Button
          variant="primary"
          onClick={onSaveOrgBudget}
          disabled={savingBudget}
        >
          {savingBudget ? 'Saving…' : 'Save org budget'}
        </Button>

        <Text variant="h4" style={{ marginTop: '0.5rem' }}>
          Application budgets
        </Text>
        {appBudgetRows.length === 0 ? (
          <Text variant="body2">No applications yet.</Text>
        ) : (
          <Table
            columns={appBudgetColumns}
            data={appBudgetRows}
            rowKey="applicationId"
          />
        )}
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Invoices</SectionTitle>
        {invoiceRows.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            description="Monthly invoices appear here after the billing period closes."
          />
        ) : (
          <Table columns={invoiceColumns} data={invoiceRows} rowKey="id" />
        )}
      </PanelCard>

      {savedMsg ? (
        <Text variant="body2" style={{ color: 'var(--success, #15803d)' }}>
          {savedMsg}
        </Text>
      ) : null}
      {actionError ? (
        <Text variant="body2" style={{ color: 'var(--error, #b91c1c)' }}>
          {actionError}
        </Text>
      ) : null}

      <SlideOver
        isOpen={modalOpen}
        onClose={closeAddCard}
        title="Add payment method"
        width="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Text variant="body2">
            Enter card details securely via Stripe. Lumina never stores raw card
            numbers.
          </Text>
          {cardError ? (
            <Text variant="body2" style={{ color: 'var(--error, #b91c1c)' }}>
              {cardError}
            </Text>
          ) : null}
          {setupLoading ? (
            <Text variant="body2">Preparing secure card form…</Text>
          ) : null}
          {clientSecret && stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: { theme: 'stripe' } }}
            >
              <SetupForm
                onDone={onCardSaved}
                onCancel={closeAddCard}
                onError={setCardError}
              />
            </Elements>
          ) : null}
          {!setupLoading && !clientSecret ? (
            <Button variant="outline" onClick={closeAddCard}>
              Close
            </Button>
          ) : null}
        </div>
      </SlideOver>
    </div>
  );
};

export default BillingPanel;
